/**
 * Translation layer between the technical errors our services throw and the
 * copy we are willing to put in front of a user.
 *
 * Why this exists: every AWS-backed call funnels through `LambdaError` /
 * `S3Error`, whose `message` is deliberately technical — it carries the
 * physical function name, the HTTP status, and whatever the handler put in the
 * body. That string is exactly what we want in Sentry and exactly what we do
 * NOT want in a modal. Before this module, `showGlobalErrorNotification` piped
 * `error.message` straight into the notification, so users saw things like:
 *
 *   AWS call failed: Lambda invocation failed
 *   (IndigoLambdaStack-prod-GenerateRecordBriefingGDocLinkFunction):
 *   returned status code 404: Failed to create Word document for appointment
 *   20ed248f-77a9-4eab-b20b-a1c9d17af4e0
 *
 * `toUserFacingError` maps any error to a `{ title, message }` pair written for
 * a clinician, and stashes the raw technical text in `devDetails`, which is
 * populated in the dev environment only. The technical text always reaches
 * Sentry regardless — see `globalErrorHandler`.
 */
import {
  ApiNetworkError,
  AWSAuthError,
  AWSError,
  ClockSkewError,
  InternalError,
  LambdaError,
  NetworkError,
  S3Error,
  UnauthorizedAccessError,
  UserError,
} from "./exceptions";
import { isGraphQLError } from "./graphqlErrors";

export interface UserFacingError {
  /** Modal heading. Short, sentence-shaped, no error taxonomy. */
  title: string;
  /** Body copy. Says what failed and what the user can do about it. */
  message: string;
  /**
   * Raw technical lines (physical function name, status code, request id,
   * original message). Only populated in the dev environment — the UI renders
   * them behind a "Dev only" badge.
   */
  devDetails?: string[];
}

const RETRY_HINT =
  "Please try again. If it keeps happening, contact support and we'll take a look.";

const GENERIC: UserFacingError = {
  title: "Something went wrong",
  message: `We couldn't complete that action. ${RETRY_HINT}`,
};

const SUPPORT_HINT = "If you think this is a mistake, contact support.";

// ---------------------------------------------------------------------------
// Naming the operation that failed
// ---------------------------------------------------------------------------

/**
 * Physical Lambda names look like
 * `IndigoLambdaStack-prod-GenerateRecordBriefingGDocLinkFunction`. Strip the
 * stack/stage prefix and the `Function` suffix to get at the logical name.
 */
const logicalLambdaName = (functionName: string): string =>
  functionName
    .split("-")
    .pop()
    ?.replace(/Function$/, "") ?? functionName;

/**
 * What the failed call was producing, in the user's vocabulary. Keyword-driven
 * rather than a per-Lambda table: there are 60+ Lambdas and they follow a
 * consistent `<Verb><Thing>` naming convention, so a table would rot the first
 * time someone adds a function and forgets to register it here.
 */
const subjectOf = (logicalName: string): string => {
  if (/GDoc|GoogleDoc|WordDoc|Docx|Doc(ument)?Link/i.test(logicalName))
    return "the document";
  if (/Pdf/i.test(logicalName)) return "the PDF";
  if (/Email/i.test(logicalName)) return "the email";
  if (/Fax|IFax/i.test(logicalName)) return "the fax";
  if (/Letter|Notice/i.test(logicalName)) return "the letter";
  if (/Report|Briefing|Recap|Summary|Summarize/i.test(logicalName))
    return "the report";
  if (/Form|Voucher|Prescription/i.test(logicalName)) return "the form";
  if (/File|Upload|Merge|Attachment|Parser/i.test(logicalName))
    return "the file";
  return "this action";
};

/** The verb that reads best for the Lambda's own prefix. */
const verbOf = (logicalName: string): string => {
  if (/^(Generate|Gen|Auto)/i.test(logicalName)) return "generate";
  if (/^Create/i.test(logicalName)) return "create";
  if (/^(Duplicate|Copy)/i.test(logicalName)) return "duplicate";
  if (/^(Send|Deliver|Emit)/i.test(logicalName)) return "send";
  if (/^(Update|Backfill|Populate|Append|Correct|Resolve)/i.test(logicalName))
    return "update";
  if (/^(Get|Fetch|List|Download|Load)/i.test(logicalName)) return "load";
  if (/^(Delete|Remove)/i.test(logicalName)) return "delete";
  if (/^(Merge|Consolidate)/i.test(logicalName)) return "merge";
  if (/^(Validate|Revalidate|Check)/i.test(logicalName)) return "validate";
  return "complete";
};

interface Operation {
  verb: string;
  subject: string;
}

const describeLambdaOperation = (functionName: string): Operation => {
  const logicalName = logicalLambdaName(functionName);
  return { verb: verbOf(logicalName), subject: subjectOf(logicalName) };
};

const titleFor = ({ verb, subject }: Operation): string =>
  subject === "this action"
    ? "Something went wrong"
    : `Couldn't ${verb} ${subject}`;

// ---------------------------------------------------------------------------
// Deciding whether a server message is safe to show
// ---------------------------------------------------------------------------

/**
 * Some handlers return genuinely actionable text — "Doctor has no phone
 * number", "Patient has no address on file" — and we lose real value by
 * swallowing it. Others return a Python `ValueError` verbatim. This is the
 * gate between the two.
 *
 * Bias is conservative: anything that smells of internals is rejected and the
 * user gets the generic copy instead (the full text still goes to Sentry).
 */
const TECHNICAL_MARKERS =
  /traceback|exception|stack ?trace|botocore|boto3|\bnull\b|\bundefined\b|\bNone\b|errorType|errorMessage|status ?code|<[a-z!/]|[{}[\]]|\bat [A-Za-z$_]+ \(|https?:\/\/|\.py\b|\.tsx?\b/i;

/** A UUID in user copy is always an internal identifier leaking out. */
const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * Openers that mark the message as a developer's failure note rather than a
 * statement about the user's data. These duplicate the friendly title anyway.
 */
const TECHNICAL_OPENERS =
  /^(failed|error|unhandled|internal|unknown|invalid|resource not found|too many requests|aws internal error|request timed out|payload too large|no message|null|undefined)\b/i;

export const isUserPresentableMessage = (
  message: string | undefined,
): message is string => {
  if (!message) return false;
  const trimmed = message.trim();
  return (
    trimmed.length > 0 &&
    trimmed.length <= 200 &&
    !trimmed.includes("\n") &&
    // A bare token ("ValidationError") is a code, not a sentence.
    trimmed.includes(" ") &&
    /^[A-Za-z]/.test(trimmed) &&
    !TECHNICAL_OPENERS.test(trimmed) &&
    !UUID_PATTERN.test(trimmed) &&
    !TECHNICAL_MARKERS.test(trimmed)
  );
};

// ---------------------------------------------------------------------------
// Lambda errors
// ---------------------------------------------------------------------------

const readExtra = (error: LambdaError, key: string): unknown =>
  error.extra?.[key];

const statusCodeOf = (error: LambdaError): number | undefined => {
  const raw = readExtra(error, "statusCode");
  return typeof raw === "number" ? raw : undefined;
};

const serverMessageOf = (error: LambdaError): string | undefined => {
  const raw = readExtra(error, "serverMessage");
  return typeof raw === "string" ? raw : undefined;
};

/**
 * Body copy for a Lambda failure, keyed on the HTTP status the handler
 * returned (or the AWS-level failure we mapped in `handleLambdaServerError`).
 */
const lambdaMessage = (
  error: LambdaError,
  operation: Operation,
): { title: string; message: string } => {
  const { verb, subject } = operation;
  const attempt = `We couldn't ${verb} ${subject}`;
  const title = titleFor(operation);

  // A handler that told us something useful about the user's data wins over
  // any status-derived copy — it's the only text here that can actually be
  // acted on.
  const serverMessage = serverMessageOf(error);
  if (isUserPresentableMessage(serverMessage)) {
    return {
      title,
      message: /[.!?]$/.test(serverMessage)
        ? serverMessage
        : `${serverMessage}.`,
    };
  }

  const status = statusCodeOf(error);
  const technicalMessage = error.message;

  if (status === 401 || status === 403) {
    return {
      title: "You don't have access to this",
      message: `You don't have permission to ${verb} ${subject}. ${SUPPORT_HINT}`,
    };
  }
  if (status === 404) {
    return {
      title,
      message: `${attempt} because some of the information it needs is missing. ${RETRY_HINT}`,
    };
  }
  if (status === 400 || status === 422) {
    return {
      title,
      message: `${attempt} — some of the information it needs is missing or invalid. Check the record for gaps, then try again.`,
    };
  }
  if (status === 429 || /too many requests|throttl/i.test(technicalMessage)) {
    return {
      title: "The service is busy",
      message: `Too many requests are in flight right now. Wait a moment, then try to ${verb} ${subject} again.`,
    };
  }
  if (status === 413 || /payload too large/i.test(technicalMessage)) {
    return {
      title,
      message: `${attempt} because the data involved is too large to process in one go.`,
    };
  }
  if (/timed out|timeout/i.test(technicalMessage)) {
    return {
      title: "This is taking too long",
      message: `${attempt} in time. It may still be processing — wait a moment and refresh before trying again.`,
    };
  }

  return {
    title,
    message: `${attempt}. Something went wrong on our end and the team has been notified. ${RETRY_HINT}`,
  };
};

// ---------------------------------------------------------------------------
// Dev-only technical detail
// ---------------------------------------------------------------------------

/**
 * Whether we're running against the dev environment.
 *
 * Reads the env var directly rather than reusing `getCurrentEnv()` from
 * `~/common/utils`: that module pulls in the Amplify / date-fns /
 * address-parsing graph, and this one sits on the error path of every service
 * call (`serviceErrorUtil` imports it). Error handling has to stay loadable
 * when things are already broken, so it stays dependency-light. Same variable
 * and same default as `getCurrentEnv`.
 */
const isDevEnvironment = (): boolean =>
  (process.env.REACT_APP_ENV || "dev") === "dev";

const pushDetail = (details: string[], label: string, value: unknown): void => {
  if (value === undefined || value === null || value === "") return;
  details.push(`${label}: ${String(value)}`);
};

/**
 * Technical lines for the dev environment. Returns `undefined` everywhere
 * else — stage and prod users never see internals, they go to Sentry instead.
 */
export const buildDevDetails = (error: unknown): string[] | undefined => {
  if (!isDevEnvironment()) return undefined;

  const details: string[] = [];

  if (error instanceof LambdaError) {
    pushDetail(details, "Function", error.functionName);
    pushDetail(details, "Status", statusCodeOf(error));
    pushDetail(details, "Request ID", readExtra(error, "requestId"));
    pushDetail(details, "Error type", readExtra(error, "errorType"));
  } else if (error instanceof S3Error) {
    pushDetail(
      details,
      "S3",
      `${error.operation} ${error.bucket}/${error.key}`,
    );
  } else if (error instanceof ApiNetworkError) {
    pushDetail(details, "Endpoint", error.endpoint);
  }

  if (error instanceof Error) {
    pushDetail(details, error.name, error.message);
    if (error instanceof AWSError && error.originalError instanceof Error) {
      pushDetail(
        details,
        "Cause",
        `${error.originalError.name}: ${error.originalError.message}`,
      );
    }
  } else {
    pushDetail(details, "Raw", String(error));
  }

  return details.length > 0 ? details : undefined;
};

// ---------------------------------------------------------------------------
// Entry points
// ---------------------------------------------------------------------------

type BaseError = Omit<UserFacingError, "devDetails">;

/**
 * Copy for the error classes we recognise. Returns `null` when the error is
 * none of them, so callers can decide between their own fallback and the
 * generic catch-all.
 */
const classify = (error: unknown): BaseError | null => {
  // Auth and clock skew are surfaced by dedicated flows in serviceErrorUtil,
  // but map them here too so direct callers (toasts) get sane copy.
  if (error instanceof AWSAuthError) {
    return {
      title: "Session expired",
      message: "Your session has expired. Refresh the page to sign back in.",
    };
  }
  if (error instanceof ClockSkewError) {
    return {
      title: "Your computer clock is out of sync",
      message:
        "Indigo can't reach the server because your computer's date/time " +
        "doesn't match. Enable automatic time-sync in your system settings, " +
        "then refresh.",
    };
  }

  if (error instanceof LambdaError) {
    return lambdaMessage(error, describeLambdaOperation(error.functionName));
  }

  if (error instanceof S3Error) {
    const isUpload = /put|upload/i.test(error.operation);
    return {
      title: isUpload ? "Couldn't upload the file" : "Couldn't open the file",
      message: isUpload
        ? `We couldn't upload that file. Check your connection, then try again. ${RETRY_HINT}`
        : `We couldn't open that file. It may still be processing. ${RETRY_HINT}`,
    };
  }

  if (error instanceof NetworkError) {
    return {
      title: "Connection problem",
      message:
        "We couldn't reach Indigo. Check your internet connection and try again.",
    };
  }

  if (error instanceof UnauthorizedAccessError) {
    return {
      title: "You don't have access to this",
      message: `You don't have permission to view this ${error.resourceType}. ${SUPPORT_HINT}`,
    };
  }

  // UserError is authored for users by definition — pass it through.
  if (error instanceof UserError) {
    return { title: "Action needed", message: error.message };
  }

  if (isGraphQLError(error)) {
    return {
      title: "Couldn't load your data",
      message: `Some information didn't come back from the server. ${RETRY_HINT}`,
    };
  }

  if (error instanceof InternalError) {
    return GENERIC;
  }

  return null;
};

/**
 * Maps any thrown value to copy that is safe to show a user.
 *
 * @example
 * const { title, message, devDetails } = toUserFacingError(error);
 * showNotification(title, message, undefined, undefined, devDetails);
 */
export const toUserFacingError = (error: unknown): UserFacingError => {
  const devDetails = buildDevDetails(error);
  const base = classify(error) ?? GENERIC;
  return devDetails ? { ...base, devDetails } : base;
};

/**
 * Single-line variant for toasts, which have no room for a title.
 *
 * `fallback` is for call sites that already have better copy for errors this
 * module doesn't classify — e.g. an api-server `Error(detail)`, where the
 * FastAPI detail is written for the user and beats the generic catch-all.
 *
 * @example
 * toast.error(toUserFacingMessage(err, "Couldn't finalize the evaluation"));
 */
export const toUserFacingMessage = (
  error: unknown,
  fallback?: string,
): string => classify(error)?.message ?? fallback ?? GENERIC.message;
